import { useState, useCallback, createContext, useContext } from "react"
import {
  useCustomAttributionControllerCreateMediaTargetMutation,
  useCustomAttributionControllerUpdateMediaTargetMutation,
  useCustomAttributionControllerDeleteMediaTargetMutation,
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerBulkCreateMediaTargetsMutation,
  useCustomAttributionControllerGetInitiativeTypesQuery,
} from "~/ui-rtk/api/customAttributionApi"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"
import { INITIATIVE_TYPE_LABELS } from "~/ui-rtk/constants/initiative-types"
import { InitiativeTypeChangeModal } from "./InitiativeTypeChangeModal"

interface MediaTarget {
  id: string
  campaign_id?: string
  ad_set_id?: string
  initiativeType?: string
  type?: string
  [key: string]: unknown
}

/** How a campaign-level change should propagate to child ad sets. */
export type CascadeMode = "all" | "empty-only"

interface RowData {
  campaign_id?: string
  adset_id?: string
  ad_set_id?: string
  /**
   * Child ad set IDs for a campaign row. When present (Budget Recommender
   * campaign rows on Meta/TikTok), confirming a change cascades to these ad
   * sets instead of writing a single campaign-level record.
   */
  childAdsetIds?: string[]
  [key: string]: unknown
}

interface InitiativeTypeContainerProps {
  children: React.ReactNode
}

// Create context for modal state
interface InitiativeTypeContextType {
  initiativeChangeModal: {
    isOpen: boolean
    rowData: RowData | null
    newValue: string
    isLoading: boolean
  }
  setInitiativeChangeModal: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean
      rowData: RowData | null
      newValue: string
      isLoading: boolean
    }>
  >
}

const InitiativeTypeContext = createContext<InitiativeTypeContextType | null>(
  null,
)

export function InitiativeTypeContainer({
  children,
}: InitiativeTypeContainerProps) {
  const currentCompany = useCurrentCompany()

  // Initiative type change modal state
  const [initiativeChangeModal, setInitiativeChangeModal] = useState<{
    isOpen: boolean
    rowData: RowData | null
    newValue: string
    isLoading: boolean
  }>({
    isOpen: false,
    rowData: null,
    newValue: "",
    isLoading: false,
  })

  const [createItem] = useCustomAttributionControllerCreateMediaTargetMutation()
  const [updateItem] = useCustomAttributionControllerUpdateMediaTargetMutation()
  const [deleteItem] = useCustomAttributionControllerDeleteMediaTargetMutation()
  const [bulkCreateItems] =
    useCustomAttributionControllerBulkCreateMediaTargetsMutation()

  // Fetch all attribution items for the current company
  const { data: allItems = [] } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  // Cascade a campaign-level change down to its child ad sets.
  // "all": overwrite every child (even differently-classified ones).
  // "empty-only": only set child ad sets that have no existing target, keeping
  // explicit assignments untouched.
  const cascadeToChildAdsets = useCallback(
    async (
      newValue: string,
      campaignId: string,
      childAdsetIds: string[],
      cascadeMode: CascadeMode,
    ) => {
      const items = allItems as MediaTarget[]
      const findAdsetTarget = (adsetId: string) =>
        items.find(item => item.ad_set_id === adsetId && item.ad_set_id !== "")

      // Clearing the campaign removes the campaign-level record and every
      // existing child ad set target.
      if (!newValue) {
        const campaignTarget = items.find(
          item =>
            item.campaign_id === campaignId &&
            (item.ad_set_id === "" || !item.ad_set_id),
        )
        const targets = [
          ...(campaignTarget ? [campaignTarget] : []),
          ...childAdsetIds
            .map(findAdsetTarget)
            .filter((t): t is MediaTarget => !!t),
        ]
        for (const target of targets) {
          await deleteItem({ id: target.id }).unwrap()
        }
        return
      }

      const companyId = currentCompany?.id
      if (!companyId) {
        throw new Error("Cannot save initiative type without a current company")
      }

      const toBulkCreate: Array<{
        initiativeType: string
        type: string
        campaign_id: string
        ad_set_id: string
      }> = []

      for (const adsetId of childAdsetIds) {
        const existing = findAdsetTarget(adsetId)
        if (existing) {
          // "empty-only" keeps explicit assignments; "all" overwrites when the
          // value actually differs (avoids redundant writes).
          if (cascadeMode === "all" && existing.initiativeType !== newValue) {
            await updateItem({
              id: existing.id,
              companyId,
              initiativeType: newValue,
              type: "adset",
              campaign_id: campaignId,
              ad_set_id: adsetId,
            }).unwrap()
          }
        } else {
          toBulkCreate.push({
            initiativeType: newValue,
            type: "adset",
            campaign_id: campaignId,
            ad_set_id: adsetId,
          })
        }
      }

      if (toBulkCreate.length > 0) {
        await bulkCreateItems({ targets: toBulkCreate }).unwrap()
      }
    },
    [allItems, currentCompany, updateItem, deleteItem, bulkCreateItems],
  )

  // Perform the actual initiative type change. Errors propagate so the modal
  // can stay open and surface the failure instead of silently closing.
  const performInitiativeTypeChange = useCallback(
    async (
      newValue: string,
      rowData: RowData,
      applyCampaignLevel = false,
      cascadeMode?: CascadeMode,
    ) => {
      const campaignId = rowData.campaign_id
      const adsetId = rowData.adset_id || rowData.ad_set_id

      // Campaign row with known child ad sets: cascade instead of writing a
      // single campaign-level record.
      if (
        cascadeMode &&
        campaignId &&
        rowData.childAdsetIds &&
        rowData.childAdsetIds.length > 0
      ) {
        await cascadeToChildAdsets(
          newValue,
          campaignId,
          rowData.childAdsetIds,
          cascadeMode,
        )
        return
      }

      // Adset row applying the change to its entire campaign, with the
      // campaign's sibling ad sets known (e.g. the Paid Media Weights Adsets
      // tab): cascade to every ad set (overwrite all) instead of writing a
      // single campaign-level record. A lone campaign-level record is masked by
      // the adset-first display priority, so the change appears to revert — this
      // mirrors the Budget Recommender's campaign-level apply.
      if (
        applyCampaignLevel &&
        campaignId &&
        rowData.childAdsetIds &&
        rowData.childAdsetIds.length > 0
      ) {
        await cascadeToChildAdsets(
          newValue,
          campaignId,
          rowData.childAdsetIds,
          "all",
        )
        return
      }

      // Find the specific item we need to update or create
      let existingItem: MediaTarget | undefined = undefined

      if (applyCampaignLevel) {
        // When applying at campaign level, always look for campaign-specific item (no adset)
        existingItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.campaign_id === campaignId &&
            (item.ad_set_id === "" || !item.ad_set_id),
        )
      } else if (adsetId) {
        // For adset rows, look for adset-specific item
        existingItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.ad_set_id === adsetId && item.ad_set_id !== "",
        )
      } else if (campaignId) {
        // For campaign rows, look for campaign-specific item (no adset)
        existingItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.campaign_id === campaignId &&
            (item.ad_set_id === "" || !item.ad_set_id),
        )
      }

      // If empty value is selected and item exists, delete it
      if (!newValue || newValue === "") {
        if (existingItem) {
          await deleteItem({ id: existingItem.id }).unwrap()
        }
        return // Exit early after deletion
      }

      const companyId = currentCompany?.id
      if (!companyId) {
        throw new Error("Cannot save initiative type without a current company")
      }

      const isAdsetLevel = !applyCampaignLevel && !!adsetId
      const itemData = {
        companyId,
        initiativeType: newValue, // The initiative type
        type: isAdsetLevel ? "adset" : "campaign",
        campaign_id: campaignId || "",
        // Only include ad_set_id if NOT applying at campaign level AND there's an adset
        ...(!applyCampaignLevel && adsetId && { ad_set_id: adsetId }),
      }

      if (existingItem) {
        await updateItem({
          id: existingItem.id,
          ...itemData,
        }).unwrap()
      } else {
        await createItem(itemData).unwrap()
      }
    },
    [
      createItem,
      updateItem,
      deleteItem,
      currentCompany,
      allItems,
      cascadeToChildAdsets,
    ],
  )

  // Handle modal confirmation - perform the actual save
  const handleModalConfirm = useCallback(
    async (applyCampaignLevel = false, cascadeMode?: CascadeMode) => {
      if (!initiativeChangeModal.rowData) return

      setInitiativeChangeModal(prev => ({ ...prev, isLoading: true }))

      try {
        await performInitiativeTypeChange(
          initiativeChangeModal.newValue,
          initiativeChangeModal.rowData,
          applyCampaignLevel,
          cascadeMode,
        )

        // Close modal on success
        setInitiativeChangeModal({
          isOpen: false,
          rowData: null,
          newValue: "",
          isLoading: false,
        })
      } catch (error) {
        // Keep modal open on error, just stop loading
        setInitiativeChangeModal(prev => ({ ...prev, isLoading: false }))
        console.error("Failed to save initiative type change:", error)
      }
    },
    [
      performInitiativeTypeChange,
      initiativeChangeModal.newValue,
      initiativeChangeModal.rowData,
    ],
  )

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setInitiativeChangeModal({
      isOpen: false,
      rowData: null,
      newValue: "",
      isLoading: false,
    })
  }, [])

  const contextValue = {
    initiativeChangeModal,
    setInitiativeChangeModal,
  }

  return (
    <InitiativeTypeContext.Provider value={contextValue}>
      {children}

      {/* Initiative Type Change Confirmation Modal */}
      <InitiativeTypeChangeModal
        isOpen={initiativeChangeModal.isOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        rowData={initiativeChangeModal.rowData ?? {}}
        childAdsetIds={initiativeChangeModal.rowData?.childAdsetIds}
        newValue={initiativeChangeModal.newValue}
        isLoading={initiativeChangeModal.isLoading}
      />
    </InitiativeTypeContext.Provider>
  )
}

// Export hooks for use in other components
export const useInitiativeTypeManagement = () => {
  const context = useContext(InitiativeTypeContext)
  if (!context) {
    throw new Error(
      "useInitiativeTypeManagement must be used within an InitiativeTypeContainer",
    )
  }

  const { setInitiativeChangeModal } = context

  const { data: allItems = [] } =
    useCustomAttributionControllerGetAllMediaTargetsQuery()

  const { data: detailedTypes = [] } =
    useCustomAttributionControllerGetInitiativeTypesQuery()
  const customTypes = detailedTypes.filter(t => t.isCustom)

  const getCurrentInitiativeType = useCallback(
    (rowData: RowData) => {
      const campaignId = rowData.campaign_id
      const adsetId = rowData.adset_id || rowData.ad_set_id

      // Priority 1: Check for adset-level override (if this is an adset row)
      if (adsetId) {
        const adsetItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.ad_set_id === adsetId && item.ad_set_id !== "",
        )
        if (adsetItem) {
          return adsetItem.initiativeType || adsetItem.type
        }
      }

      // Priority 2: Check for campaign-level setting
      if (campaignId) {
        const campaignItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.campaign_id === campaignId &&
            (item.ad_set_id === "" || !item.ad_set_id),
        )
        if (campaignItem) {
          return campaignItem.initiativeType || campaignItem.type
        }
      }

      return "" // No current value
    },
    [allItems],
  )

  const getInitiativeTypeOptions = useCallback(
    (rowData: RowData) => {
      const campaignId = rowData.campaign_id
      const adsetId = rowData.adset_id || rowData.ad_set_id

      // Check if there's an existing item for this row
      let existingItem: MediaTarget | undefined = undefined
      if (adsetId) {
        existingItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.ad_set_id === adsetId && item.ad_set_id !== "",
        )
      } else if (campaignId) {
        existingItem = (allItems as MediaTarget[]).find(
          (item: MediaTarget) =>
            item.campaign_id === campaignId &&
            (item.ad_set_id === "" || !item.ad_set_id),
        )
      }

      const options = [
        {
          value: "",
          label: existingItem
            ? "Clear Initiative Type"
            : "Select Initiative Type...",
        },
        {
          value: InitiativeType.RETENTION,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.RETENTION],
        },
        {
          value: InitiativeType.DEMAND_GENERATION,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION],
        },
        {
          value: InitiativeType.DEMAND_GENERATION_2,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_GENERATION_2],
        },
        {
          value: InitiativeType.DEMAND_CAPTURE_ACQUISITION,
          label:
            INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_ACQUISITION],
        },
        {
          value: InitiativeType.DEMAND_CAPTURE_RETARGETING,
          label:
            INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_RETARGETING],
        },
        {
          value: InitiativeType.DEMAND_CAPTURE_BROAD,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.DEMAND_CAPTURE_BROAD],
        },
        {
          value: InitiativeType.BRANDED_SEARCH,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.BRANDED_SEARCH],
        },
        {
          value: InitiativeType.NON_BRAND_SEARCH,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.NON_BRAND_SEARCH],
        },
        {
          value: InitiativeType.PMAX,
          label: INITIATIVE_TYPE_LABELS[InitiativeType.PMAX],
        },
        ...customTypes.map(t => ({
          value: t.value,
          label: t.label,
        })),
      ]

      return options
    },
    [allItems, customTypes],
  )

  // Handle initiative type changes - show confirmation modal instead of saving directly
  const handleInitiativeTypeChange = useCallback(
    (newValue: string, rowData: RowData) => {
      setInitiativeChangeModal({
        isOpen: true,
        rowData,
        newValue,
        isLoading: false,
      })
    },
    [setInitiativeChangeModal],
  )

  return {
    getCurrentInitiativeType,
    getInitiativeTypeOptions,
    handleInitiativeTypeChange,
  }
}

export default InitiativeTypeContainer
